import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileSpreadsheet, ArrowLeft } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const LABEL_CLS = {
  D: "bg-emerald-50 text-emerald-700", A: "bg-sky-50 text-sky-700",
  OFF: "bg-amber-100 text-amber-800", RO: "bg-violet-100 text-violet-800",
  SO: "bg-fuchsia-100 text-fuchsia-800", PH: "bg-rose-100 text-rose-700",
  AO: "bg-slate-200 text-slate-600",
};

export default function PackingRoster() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const year = Number(params.get("year")) || new Date().getFullYear();
  const month = Number(params.get("month")) || (new Date().getMonth() + 1);
  const [roster, setRoster] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/hr/schedule/roster?year=${year}&month=${month}`)
      .then(r => setRoster(r.data)).catch(e => setErr(e.response?.data?.detail || "Failed to load roster"));
  }, [year, month]);

  const exportExcel = async () => {
    const res = await api.get(`/hr/schedule/roster.xlsx?year=${year}&month=${month}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url;
    a.download = `packing_roster_${year}_${String(month).padStart(2, "0")}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const dayNum = (iso) => iso.slice(8);
  const dow = (iso) => ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][new Date(iso + "T00:00:00").getDay()];

  return (
    <div className="p-6 print:p-0" data-testid="packing-roster-page">
      <style>{`@media print { @page { size: A4 landscape; margin: 8mm; } .no-print { display:none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      <div className="flex items-center justify-between mb-4 no-print">
        <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
        <div className="flex gap-2">
          <Button onClick={exportExcel} variant="outline" data-testid="roster-page-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Excel</Button>
          <Button onClick={() => window.print()} data-testid="roster-page-print" className="bg-slate-900"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
        </div>
      </div>

      {err && <div className="text-rose-600 text-sm">{err}</div>}
      {roster && (
        <div>
          <div className="text-center mb-3">
            <h1 className="font-heading text-xl font-bold">Packing Team — Monthly Roster</h1>
            <div className="text-slate-500 text-sm">{MONTHS[month - 1]} {year}</div>
          </div>
          <table className="w-full border-collapse text-[10px]" data-testid="roster-table">
            <thead>
              <tr>
                <th className="border border-slate-300 bg-slate-800 text-white p-1 text-left sticky left-0">Employee</th>
                {roster.dates.map(iso => {
                  const wd = dow(iso);
                  return <th key={iso} className={`border border-slate-300 p-0.5 text-center ${wd === "Su" ? "bg-violet-100" : "bg-slate-100"}`}>
                    <div className="font-semibold">{dayNum(iso)}</div><div className="text-[8px] text-slate-500">{wd}</div>
                  </th>;
                })}
              </tr>
            </thead>
            <tbody>
              {roster.employees.map(row => (
                <tr key={row.employee_id} data-testid={`roster-row-${row.employee_id}`}>
                  <td className="border border-slate-300 p-1 whitespace-nowrap sticky left-0 bg-white">
                    <span className="font-mono font-semibold">{row.employee_id}</span>
                    <div className="text-[8px] text-slate-500">{row.full_name}</div>
                  </td>
                  {roster.dates.map(iso => {
                    const c = row.cells[iso] || {};
                    return <td key={iso} className={`border border-slate-200 p-0.5 text-center font-semibold ${LABEL_CLS[c.label] || ""}`}
                      title={c.shift_name || c.status || ""}>{c.label || ""}</td>;
                  })}
                </tr>
              ))}
              {roster.employees.length === 0 && <tr><td colSpan={roster.dates.length + 1} className="border p-4 text-center text-slate-400">No active packing staff.</td></tr>}
            </tbody>
          </table>
          <div className="flex flex-wrap gap-3 mt-3 text-[10px]">
            {Object.entries(roster.legend).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1"><span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${LABEL_CLS[k] || ""}`}>{k}</span>{v}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
